<script setup lang="ts">
/**
 * Welcome / login tab. Handles both the pre-login landing and the OAuth2
 * callback from Keycloak.
 *
 * On mount: if `parseAuthResponse` finds a Keycloak callback fragment in the
 * URL, it exchanges the auth code via `fetchUserInfo`; otherwise it calls
 * `fetchCurrentUser` to re-hydrate an existing BFF session (e.g. after an
 * in-tab reload). `displayName` derives the greeting name from the loaded
 * `userInfo` and falls back to `"there"` while unauthenticated.
 */
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { redirectToLogin, parseAuthResponse } from '@/api/keycloak_api'
import { fetchUserInfo, fetchCurrentUser } from '@/api/bff_api'
import { useUserStore } from '@/stores/user_store'
import HappySmileIcon from '@/components/helpers/HappySmileIcon.vue'
import { useIsMobile } from '@/composables/useIsMobile'

const userStore = useUserStore()
const { userInfo, isLoggedInProfileReady } = storeToRefs(userStore)
const { isMobile } = useIsMobile()
const { t } = useI18n()

const loading = ref(true)
const error = ref<string | undefined>()

const displayName = computed(() => {
  const u = userInfo.value
  if (!u) return t('tabs.welcome.fallbackName')
  const fromNames = `${u.first_name} ${u.last_name}`.trim()
  if (fromNames) return fromNames
  if (u.preferred_username?.trim()) return u.preferred_username.trim()
  return t('tabs.welcome.fallbackName')
})

onMounted(async () => {
  const auth = parseAuthResponse()
  try {
    if (auth) {
      // Returning from the Keycloak redirect: exchange the auth code for the session.
      const response = await fetchUserInfo({ authCode: auth.code, nonce: auth.nonce })
      userStore.setUserInfoFromUserinfoResponse(response)
    } else if (!isLoggedInProfileReady.value) {
      // Direct visit (or in-tab reload that wiped the in-memory store): ask the BFF whether the
      // session cookie still resolves to a signed-in user. `fetchCurrentUser` returns `null` for
      // the expected "not authenticated yet" case so we silently fall through to the logged-out
      // UI instead of surfacing an error.
      const me = await fetchCurrentUser()
      if (me) {
        userStore.setUserInfo(me)
      }
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : t('tabs.welcome.signInFailed')
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <article :class="{ 'article--mobile': isMobile }">
    <div :class="{ 'welcome-body--mobile': isMobile }">
      <h1>{{ t('tabs.welcome.title') }}</h1>
      <p class="subtitle" :class="{ 'subtitle--mobile': isMobile }">
        {{ t('tabs.welcome.subtitle') }}
      </p>

      <section v-if="loading" class="loading-state">
        <img src="/loading-tufte.gif" alt="" class="loading-tufte-gif" width="240" height="24" />
        <p class="text-tufte-muted">{{ t('tabs.welcome.authenticating') }}</p>
      </section>

      <section v-else-if="error">
        <p class="text-red-600">{{ error }}</p>
        <button class="tufte-button" @click="redirectToLogin">
          {{ t('tabs.welcome.tryAgain') }}
        </button>
      </section>

      <section
        v-else-if="isLoggedInProfileReady"
        class="welcome-state"
        :class="{ 'welcome-state--mobile': isMobile }"
      >
        <HappySmileIcon
          class="welcome-smile"
          :size="isMobile ? '100px' : 'var(--size-welcome-emoji)'"
        />
        <h2 :class="{ 'greeting--mobile': isMobile }">
          {{ t('tabs.welcome.greeting', { name: displayName }) }}
        </h2>
        <p>{{ t('tabs.welcome.encouragement') }}</p>
      </section>

      <section v-else>
        <h2>{{ t('tabs.welcome.gettingStarted') }}</h2>
        <p>{{ t('tabs.welcome.loginPrompt') }}</p>
        <button class="tufte-button" @click="redirectToLogin">
          {{ t('tabs.welcome.goToLogin') }}
        </button>
      </section>
    </div>
  </article>
</template>

<style scoped>
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}
.loading-tufte-gif {
  display: block;
  max-width: 100%;
  height: auto;
}
.welcome-state {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}
.welcome-smile {
  margin-top: 0.5rem;
}

/* ── mobile layout ─────────────────────────────────────────────────────────── */

.article--mobile {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 1.75rem;
}

.welcome-body--mobile {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 0.6rem;
  padding-bottom: 5%;
}

.welcome-body--mobile h1 {
  font-size: 2.75rem;
  line-height: 1.05;
  letter-spacing: -0.012em;
}

.subtitle--mobile {
  font-style: italic;
  font-size: 1.375rem;
  line-height: 1.3;
  margin-bottom: 0.4rem;
}

.welcome-state--mobile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
}

.welcome-state--mobile .welcome-smile {
  margin-top: 0;
  margin-bottom: 0.6rem;
}

.greeting--mobile {
  font-style: italic;
  font-size: 1.75rem;
  line-height: 1.2;
}

.welcome-body--mobile p:not(.text-tufte-muted) {
  font-size: 1.25rem;
  line-height: 1.4;
}
</style>
